import { makeAutoObservable } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import {
  type IQuickLinkTemplate,
  QuickLinkPayload,
  QuickLinkApi,
  QuickLinkTemplate,
} from 'entities/QuickLink'
import { complianceCheckUrl } from 'entities/Compliance'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { QuickLinkModal } from 'pages/settings/pages/quickLinks/ui/QuickLinkModal'

const ERROR_MESSAGE = 'The URL you entered could not be reached.'
export class QuickLinkOperationStore {
  private _createQuickLinkModalId = 'createQuickLinkTemplateModalId'
  private _editQuickLinkModalId = 'editQuickLinkTemplateModalId'
  private _deleteQuickLinkModalId = 'deleteQuickLinkTemplateModalId'

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  create = (): Promise<IQuickLinkTemplate | null> =>
    new Promise((resolve) => {
      const payload = new QuickLinkPayload()

      const onClose = () => {
        modalStore.removeModal(this._createQuickLinkModalId)
        resolve(null)
      }

      const onComplete = async () => {
        if (!payload.mergeFieldsCount) {
          const complianceValue = await complianceCheckUrl(payload.url)

          if (complianceValue.url) {
            payload.setUrl(complianceValue.url)
          } else {
            payload.setErrorUrlMessage(ERROR_MESSAGE)
            return
          }
        }
        try {
          const { data } = await QuickLinkApi.createLinkTemplate(payload.toJSON())

          modalStore.removeModal(this._createQuickLinkModalId)
          toastStore.add({
            type: 'success',
            title: 'Quick Link created',
          })

          return resolve(new QuickLinkTemplate(data))
        } catch (error) {
          logger.error(error)
        }
      }

      modalStore.addModal({
        id: this._createQuickLinkModalId,
        title: 'Add link',
        pureContent: true,
        ModalContent: QuickLinkModal,
        zIndex: ZIndex.OVERLAY_MIDDLE,
        ModalContentProps: {
          completeText: 'Add',
          payload,
          onComplete,
          onClose,
          entities: this._entities,
        },
        width: 480,
        disabledOverlayClose: true,
        onClose,
      })
    })

  edit = (template: IQuickLinkTemplate): Promise<IQuickLinkTemplate | null> =>
    new Promise((resolve) => {
      const payload = template.payload

      const onClose = () => {
        modalStore.removeModal(this._editQuickLinkModalId)
        resolve(null)
      }

      const onComplete = async () => {
        if (!payload.mergeFieldsCount) {
          const complianceValue = await complianceCheckUrl(payload.url)

          if (complianceValue.url) {
            payload.setUrl(complianceValue.url)
          } else {
            payload.setErrorUrlMessage(ERROR_MESSAGE)
            return
          }
        }
        try {
          const { data } = await QuickLinkApi.updateLinkTemplate(template.id, payload.toJSON())
          template.syncOrigin(data)

          modalStore.removeModal(this._editQuickLinkModalId)

          return resolve(template)
        } catch (error) {
          logger.error(error)
        }
      }

      modalStore.addModal({
        id: this._editQuickLinkModalId,
        title: 'Edit link',
        pureContent: true,
        ModalContent: QuickLinkModal,
        zIndex: ZIndex.OVERLAY_MIDDLE,
        ModalContentProps: {
          completeText: 'Update',
          payload,
          onComplete,
          onClose,
          entities: this._entities,
        },
        width: 480,
        disabledOverlayClose: true,
        onClose,
      })
    })

  delete = (id: number): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      const handleDelete = () => {
        modalStore.removeModal(this._deleteQuickLinkModalId)
        QuickLinkApi.deleteLinkTemplate(id).then(
          () => resolve(true),
          () => resolve(false)
        )
      }

      const handleCancel = () => {
        modalStore.removeModal(this._deleteQuickLinkModalId)
        resolve(false)
      }

      modalStore.addModal({
        id: this._deleteQuickLinkModalId,
        showHeader: true,
        showCloseButton: false,
        showCloseIcon: true,
        width: 280,
        type: ModalTypeList.ALERT,
        zIndex: ZIndex.OVERLAY_MIDDLE,
        title: 'Delete link?',
        desc: 'This action cannot be undone',
        primaryAction: {
          text: 'Delete',
          onAction: handleDelete,
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: handleCancel,
        },
        onClose: handleCancel,
      })
    })

  dispose = () => {
    modalStore.removeModal(this._deleteQuickLinkModalId)
    modalStore.removeModal(this._editQuickLinkModalId)
    modalStore.removeModal(this._createQuickLinkModalId)
  }
}
